import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DropResult } from 'react-beautiful-dnd';
import {
  Box,
  IconButton,
  Checkbox,
  Typography,
  Tooltip,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/Check';
import SortIcon from '@mui/icons-material/Sort';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SortableList from '../layout/SortableList';
import ApiClient from '../../loopback/apiClient';
import {
  deleteDigitalSection,
  updateDigitalSection,
  addDigitalSection,
  setDigitalSections,
  showErrorNotification,
  setDigitalSurvey,
  setExtendedReview,
} from '../../redux/actions';
import {
  selectDigitalSections,
} from '../../redux/reducers/SurveyDataReducer';
import { DigitalSection, RootState } from '../../redux/types';
import Prompt from '../layout/Prompt';
import { getLastSortIndex } from '../../utilities/arrayHelper';
import useGetTrialById from '../../hooks/trial/useGetTrialById';
import SurveyOptionContext from '../../views/admin/SurveyOptionContext';

type Props = PropsFromRedux & {
  onReorder?: (updateIndices: () => void) => void;
  submissionExist?: boolean;
  labelText?: string;
  showMultipleSectionSelectionCheckBox?: boolean;
  onSaveSectionOrder: () => void;
};

type TextblockReviewData = {
  present: boolean;
  extendedReviewId?: number;
  textblockReviewComment?: boolean;
}

const DigitalSectionOrganizer: React.FC<Props> = ({
  digitalSurvey,
  digitalSections,
  onReorder,
  labelText = 'Antibody',
  submissionExist,
  showMultipleSectionSelectionCheckBox,
  deleteDigitalSection: deleteDigitalSectionAction,
  updateDigitalSection: updateDigitalSectionAction,
  addDigitalSection: addDigitalSectionAction,
  setDigitalSections: setDigitalSectionsAction,
  showErrorNotification: showErrorNotificationAction,
  setExtendedReview: setExtendedReviewAction,
  onSaveSectionOrder,
}: Props) => {
  const [sectionTextAddBuffer, setSectionTextAddBuffer] = useState<string>('');
  const [sortMode, setSortMode] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [warningTitle, setWarningTitle] = useState<string>('');
  const [deleteSectionId, setDeleteSectionId] = useState<number>(NaN);
  const [editSectionBuffer, setEditSectionBuffer] = useState<DigitalSection>();
  const [
    isMultipleAntibodySelection,
    setIsMultipleAntibodySelection,
  ] = useState<boolean>(digitalSurvey?.allowMultipleSectionSubmission || false);
  const [
    textblockReviewPresent, setTextblockReviewPresent,
  ] = useState<TextblockReviewData>({ present: false });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { trialId } = digitalSurvey || {};
  const trial = useGetTrialById(trialId);
  const { disableEdit } = useContext(SurveyOptionContext);

  /** check if Textblock review is activated */
  useEffect(() => {
    async function checkTextblockReviewExist() {
      if (!trial) {
        return;
      }
      const extendedReviews = await ApiClient.getExtendedReview(trial.id);
      const textBlockComments = await ApiClient.checkTextblockReviewCommentExists(trial.id);
      if (extendedReviews[0] && textBlockComments.count) {
        setTextblockReviewPresent({
          present: true,
          extendedReviewId: extendedReviews[0].id,
          textblockReviewComment: !!textBlockComments,
        });
      }
    }
    if (trial?.reviewCategoryId === 3) {
      checkTextblockReviewExist();
    }
  }, [trial]);

  const deleteTextblockReviewComments = useCallback(() => {
    const { extendedReviewId, textblockReviewComment } = textblockReviewPresent;
    const funcArr: (() => Promise<void>)[] = [];
    if (extendedReviewId) {
      funcArr.push(ApiClient.deleteExtendedReview.bind(null, extendedReviewId));
    }
    if (textblockReviewComment && trial) {
      funcArr.push(ApiClient.deleteAllTextblockCommentsByTrialId.bind(null, trial.id));
    }
    const promiseArr = funcArr.map((f) => f());
    Promise.all(promiseArr)
      .then(() => {
        setExtendedReviewAction(undefined);
        setTextblockReviewPresent({ present: false });
      });
  }, [trial, textblockReviewPresent, setExtendedReviewAction]);

  /** Update in redux and backend on save */
  const updateSortIndices = useCallback((sortedList: DigitalSection[]) => {
    const updatedList = sortedList.map((i, index) => {
      const updatedSortIndex: DigitalSection = { ...i, sortIndex: index };
      return ApiClient.updateDigitalSection(updatedSortIndex);
    });
    Promise.all(updatedList)
      .then((uList) => {
        setDigitalSectionsAction(uList);
        deleteTextblockReviewComments();
      }).catch((e) => {
        showErrorNotificationAction(JSON.stringify(e));
      });
  }, [
    setDigitalSectionsAction,
    showErrorNotificationAction,
    deleteTextblockReviewComments,
  ]);

  const onDragHandler = useCallback((result: DropResult, sortedList?: DigitalSection[]) => {
    if (onReorder && sortedList) {
      onReorder(updateSortIndices.bind(null, sortedList));
    }
  }, [onReorder, updateSortIndices]);

  const onSectionAddTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSectionTextAddBuffer(value);
  };

  const onAddDigitalSection = () => {
    if (submissionExist) {
      showErrorNotificationAction('Cannot add new section because submission(s) data has been created by participants');
      return;
    }
    if (digitalSurvey) {
      const sortIdx = getLastSortIndex<DigitalSection>(digitalSections);
      addDigitalSectionAction({
        digitalSurveyId: digitalSurvey.id,
        name: sectionTextAddBuffer,
        sortIndex: sortIdx,
      });
      setSectionTextAddBuffer('');
      deleteTextblockReviewComments();
    }
  };

  const onAllowMultipleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsMultipleAntibodySelection(event.target.checked);
    if (digitalSurvey) {
      ApiClient.updateDigitalSurvey({
        ...digitalSurvey,
        allowMultipleSectionSubmission: event.target.checked,
      }).then((ds) => {
        dispatch(setDigitalSurvey(ds));
      });
    }
  };

  const onTextEnterHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && digitalSurvey) {
      onAddDigitalSection();
    }
  };

  const onDeleteSection = useCallback((section: DigitalSection) => {
    ApiClient.getDigitalSubmissionMetaBySectionId({ selectedSectionId: section.id })
      .then((digitalSubmissionMetas) => {
        const { name } = section;
        setWarningTitle(`Delete Section: ${name}`);
        if (digitalSubmissionMetas.length) {
          showErrorNotificationAction(`Cannot delete section: ${name} because submission(s) data has been created by participants`);
          return;
        }
        setWarningMessage(`Are you sure you want to delete this section: ${name}?`);
        setDeleteSectionId(section.id);
      });
  }, [showErrorNotificationAction]);

  const onSectionBufferNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (editSectionBuffer) {
      setEditSectionBuffer({
        ...editSectionBuffer,
        name,
      });
    }
  };

  const onUpdateSection = useCallback(() => {
    if (submissionExist) {
      showErrorNotificationAction('Cannot update section because submission(s) data has been created by participants');
      return;
    }
    if (editSectionBuffer) {
      updateDigitalSectionAction(editSectionBuffer);
    }
    setEditSectionBuffer(undefined);
  }, [
    editSectionBuffer,
    setEditSectionBuffer,
    updateDigitalSectionAction,
    showErrorNotificationAction,
    submissionExist,
  ]);

  const editSectionHandler = (sectionObj: DigitalSection) => {
    setEditSectionBuffer({ ...sectionObj });
  };

  const cancelEditHandler = () => {
    setEditSectionBuffer(undefined);
  };

  const cancelSortHandler = () => {
    setSortMode(false);
  };

  const saveSortHandler = () => {
    onSaveSectionOrder();
    setSortMode(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>{`Setup ${labelText}`}</Typography>
      {
        textblockReviewPresent.present && (
          <Alert severity="warning">
            Textblock comments are present. Adding, deleting or re-ordering
            the sections will remove all Textblock comments from the review.
          </Alert>
        )
      }
      {
        !sortMode ? (
          <Box display="flex" sx={{ alignItems: 'center' }}>
            <Typography fontWeight={500} sx={{ flexGrow: 1 }}>
              Add, Update or Delete Sections
            </Typography>
            {!showMultipleSectionSelectionCheckBox && (
              <Tooltip title="Copy section" placement="top">
                <>
                  <IconButton
                    onClick={() => {
                      navigate(`/trial/${trialId}/survey/${digitalSurvey?.id}/editor/copy`);
                    }}
                    disabled={disableEdit}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </>
              </Tooltip>
            )}
            <Tooltip title="Sort" placement="top">
              <>
                <IconButton
                  disabled={disableEdit}
                  onClick={() => setSortMode(true)}
                >
                  <SortIcon />
                </IconButton>
              </>
            </Tooltip>
          </Box>
        ) : (
          <Box display="flex" gap={1} sx={{ alignItems: 'center' }}>
            <Typography fontWeight={500} sx={{ flexGrow: 1 }}>Reorder Cases</Typography>
            <Button size="small" variant="outlined" onClick={saveSortHandler}>Save</Button>
            <Button size="small" variant="outlined" color="secondary" onClick={cancelSortHandler}>Cancel</Button>
          </Box>
        )
      }
      {
        !sortMode ? (
          digitalSections.map((ds: DigitalSection) => (
            <Box key={ds.id} sx={{ m: '10px' }}>
              {
                editSectionBuffer && editSectionBuffer.id === ds.id ? (
                  <Box display="flex" aria-label="case-edit" alignItems="center">
                    <Box sx={{ py: 1 }} flexGrow={1}>
                      <TextField onChange={onSectionBufferNameChange} value={editSectionBuffer.name} size="small" fullWidth label="Update Section" />
                    </Box>
                    <Box>
                      <IconButton onClick={cancelEditHandler}>
                        <CancelOutlinedIcon />
                      </IconButton>
                      <IconButton onClick={onUpdateSection}>
                        <CheckIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box display="flex" aria-label="case-view">
                    <Box sx={{ py: 1 }} flexGrow={1}>
                      <Typography>{ds.name}</Typography>
                    </Box>
                    <Box>
                      <>
                        <IconButton disabled={disableEdit} onClick={() => editSectionHandler(ds)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton disabled={disableEdit} onClick={() => onDeleteSection(ds)}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    </Box>
                  </Box>
                )
              }
            </Box>
          ))
        ) : (
          <SortableList<DigitalSection>
            list={digitalSections}
            droppableId="casesOrganizer"
            onDragEndHandler={onDragHandler}
          />
        )
      }
      <Box>
        {!sortMode && (
          <TextField
            fullWidth
            size="small"
            label={`Add ${labelText}`}
            value={sectionTextAddBuffer}
            onChange={onSectionAddTextChange}
            onKeyDown={onTextEnterHandler}
            disabled={disableEdit}
          />
        )}
      </Box>
      {
        showMultipleSectionSelectionCheckBox && (
          <div className="pt3">
            <Typography>
              Allow participants to select more than one antibody
              <Checkbox
                checked={isMultipleAntibodySelection}
                onChange={onAllowMultipleSelectionChange}
              />
            </Typography>
          </div>
        )
      }
      <Prompt
        display={!!warningMessage}
        alert={!deleteSectionId}
        action={(confirm: boolean): void => {
          setWarningMessage('');
          setWarningTitle('');
          if (!confirm || !deleteSectionId) return;
          deleteDigitalSectionAction(deleteSectionId);
          deleteTextblockReviewComments();
        }}
        title={warningTitle}
        version={2}
      >
        <Typography>{warningMessage}</Typography>
      </Prompt>
    </Box>
  );
};

const mapStateToProps = (state: RootState) => ({
  digitalSurvey: state.SurveyData.survey,
  digitalSections: selectDigitalSections(state),
});

const dispatchProps = {
  deleteDigitalSection,
  updateDigitalSection,
  addDigitalSection,
  setDigitalSections,
  setExtendedReview,
  showErrorNotification,
};

const connector = connect(mapStateToProps, dispatchProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DigitalSectionOrganizer);
